// biome-ignore-all lint/suspicious/noExplicitAny: <intentionally any>
import type { HttpInstance } from '../types.ts';
import type { AxiosInterceptorManager, AxiosResponse } from 'axios';
import { useHttpInstance } from './use-http-instance.ts';

type HttpConfigureFn = (http: HttpInstance) => unknown;

function useConfigureHttp(): (configureFn: HttpConfigureFn) => void {
  const httpInstance = useHttpInstance();
  return (configureFn: HttpConfigureFn) => configureFn(httpInstance);
}

export type HttpRequestHeaderOptions = Record<string, string>;

export function useHttpRequestHeaders() {
  const configureHttp = useConfigureHttp();

  const setHeaders = (headers: HttpRequestHeaderOptions) => {
    configureHttp((http: HttpInstance) => {
      http.defaults.headers.common = {
        ...http.defaults.headers.common,
        ...headers,
      };
    });
  };

  const deleteHeader = (headerKey: string) => {
    configureHttp((http: HttpInstance) => {
      delete http.defaults.headers.common[headerKey];
    });
  };
  return {
    setHeaders,
    deleteHeader,
  };
}

type ResponseInterceptorManager<T = any> = AxiosInterceptorManager<
  AxiosResponse<T>
>;

export type HttpResponseInterceptorFn<T = any> = Parameters<
  ResponseInterceptorManager<T>['use']
>[0];

export type HttpResponseErrorInterceptorFn<T = any> = Parameters<
  ResponseInterceptorManager<T>['use']
>[1];

export function useHttpResponseInterceptor<T = any>(
  interceptorFn: HttpResponseInterceptorFn<T>,
): void {
  const configureHttp = useConfigureHttp();

  configureHttp((http) => {
    (http.interceptors.response as ResponseInterceptorManager<T>).use(
      interceptorFn,
    );
  });
}

export function useHttpResponseErrorInterceptor<T = any>(
  interceptorFn: HttpResponseErrorInterceptorFn<T>,
): void {
  const configureHttp = useConfigureHttp();

  configureHttp((http) => {
    (http.interceptors.response as ResponseInterceptorManager<T>).use(
      null,
      interceptorFn,
    );
  });
}
